/**
 * @fileOverview Conversions between the backend's merged capacity ranges and per-day maps.
 *
 * All dates are ISO day strings (`YYYY-MM-DD`) and are stepped in UTC, so a day never
 * shifts across a daylight-saving change.
 */

import { isoDateWeekday } from "@/services/date"

/** Capacity range as returned by the API: consecutive days with the same value */
export interface CapacityRange {
  from: string
  to: string
  capacity: number
}

/** One span of the `/project/assign-capacity` request; `null` resets the days to the phase average */
export interface CapacitySpan {
  fromDate: string
  toDate: string
  capacity: number | null
}

/**
 * What a phase plans on `date` without an explicit value: its average on the project's
 * work days, nothing on days off — as the backend's `phaseCapacityByDate` computes it.
 */
export function averageCapacityOn(date: string, workDays: string[] | undefined, average: number | undefined): number {
  return (workDays ?? []).includes(isoDateWeekday(date) ?? "") ? (average ?? 0) : 0
}

/** The day after `date` */
export function nextDay(date: string): string {
  const d = new Date(date)
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().split("T")[0]
}

/** `date` moved by `days` (negative: back) */
export function addDays(date: string, days: number): string {
  const d = new Date(date)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().split("T")[0]
}

/** Monday to Sunday of the week `date` lies in */
export function weekOf(date: string): { from: string; to: string } {
  const d = new Date(date)
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  const from = d.toISOString().split("T")[0]
  d.setUTCDate(d.getUTCDate() + 6)
  return { from, to: d.toISOString().split("T")[0] }
}

/** Every day from `from` to `to`, both included; empty when `to` lies before `from` */
export function daysBetween(from: string, to: string): string[] {
  const days: string[] = []
  for (let d = from; d <= to; d = nextDay(d)) days.push(d)
  return days
}

/**
 * Expands capacity ranges from the API into a per-day lookup map.
 * The API sends merged ranges (payload stays small regardless of the
 * calendar span); the expansion happens once per data change.
 */
export function expandRangesToDayMap(ranges: CapacityRange[] | undefined): Record<string, number> {
  const map: Record<string, number> = {}
  for (const range of ranges || []) {
    for (const day of daysBetween(range.from, range.to)) map[day] = range.capacity
  }
  return map
}

/** Merges a per-day map back into ranges of consecutive days with the same value */
export function mergeDayMapToRanges<T extends number | null = number>(
  map: Record<string, T>
): Array<{ from: string; to: string; capacity: T }> {
  const ranges: Array<{ from: string; to: string; capacity: T }> = []
  for (const day of Object.keys(map).sort()) {
    const last = ranges[ranges.length - 1]
    if (last && last.capacity === map[day] && nextDay(last.to) === day) last.to = day
    else ranges.push({ from: day, to: day, capacity: map[day] })
  }
  return ranges
}

/** The request spans for a set of per-day values (the same merge, in the request's field names) */
export function toCapacitySpans(valuesByDate: Record<string, number | null>): CapacitySpan[] {
  return mergeDayMapToRanges(valuesByDate).map(r => ({ fromDate: r.from, toDate: r.to, capacity: r.capacity }))
}
